import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import Draggable from 'react-draggable';

const CaptionsCustomizationModal = ({ isOpen, onClose, appearance, styles, content, currentTime, onUpdate, onPreviewUpdate }) => {
    const [fontSize, setFontSize] = useState(50);
    const [fontFamily, setFontFamily] = useState('Arial Black');
    const [highlightBg, setHighlightBg] = useState('#ff8800');
    const [editedContent, setEditedContent] = useState([]);

    // Store original appearance to revert on cancel
    const initialAppearanceRef = React.useRef(null);

    // Find the active paragraph based on currentTime
    const activeParagraphIndex = useMemo(() => {
        if (!editedContent || editedContent.length === 0) return -1;
        return editedContent.findIndex(p =>
            currentTime >= p.startTime && currentTime < (p.startTime + p.duration)
        );
    }, [editedContent, currentTime]);

    const activeParagraph = activeParagraphIndex >= 0 ? editedContent[activeParagraphIndex] : null;

    useEffect(() => {
        if (isOpen && initialAppearanceRef.current === null) {
            // we use undefined as the sentinel for "not yet set" but wait, ref initialized to null!
            // Actually, if we just want to track "has been set", let's use a separate flag or just store it.
            // If appearance was undefined, it's now stored as undefined.
            initialAppearanceRef.current = { hasSet: true, value: appearance };
        }
        if (!isOpen) {
            initialAppearanceRef.current = null;
        }
    }, [isOpen, appearance]);

    useEffect(() => {
        if (appearance) {
            setFontSize(Math.round((appearance.scale || 1) * 50));
        }
        if (styles) {
            setFontFamily(styles.fontFamily || 'Arial Black');
            setHighlightBg(styles.highlightBg || '#ff8800');
        } else {
            setHighlightBg('#ff8800');
        }
        if (content && Array.isArray(content)) {
            // Deep clone content for editing
            setEditedContent(JSON.parse(JSON.stringify(content)));
        }
    }, [appearance, styles, content, isOpen]);

    const handleSentenceChange = (newSentence) => {
        if (activeParagraphIndex < 0) return;

        setEditedContent(prev => {
            return prev.map((paragraph, pIdx) => {
                if (pIdx !== activeParagraphIndex) return paragraph;

                const newWords = newSentence.split(/\s+/).filter(w => w.length > 0);
                const oldWords = paragraph.words || [];

                // Re-map words: preserve timing from original words where possible
                const updatedWords = newWords.map((word, idx) => {
                    if (idx < oldWords.length) {
                        // Preserve timing from original word at same index
                        return { ...oldWords[idx], content: word };
                    }
                    // New word added — inherit timing from last original word
                    const lastWord = oldWords[oldWords.length - 1];
                    return {
                        content: word,
                        startTime: lastWord?.startTime || 0,
                        endTime: lastWord?.endTime || 0,
                    };
                });

                // Update the paragraph text too
                return {
                    ...paragraph,
                    words: updatedWords,
                    text: newSentence
                };
            });
        });
    };

    const handleSave = () => {
        onUpdate({
            appearance: {
                ...appearance,
                scale: fontSize / 50
            },
            styles: {
                ...styles,
                fontFamily: fontFamily,
                highlightBg: highlightBg,
                fontSize: fontSize,
            },
            content: editedContent
        });
        onClose();
    };

    const handleCancel = () => {
        if (onPreviewUpdate && initialAppearanceRef.current?.hasSet) {
            // Restore original appearance
            onPreviewUpdate(initialAppearanceRef.current.value);
        }
        onClose();
    };

    const nodeRef = React.useRef(null);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
            <Draggable
                nodeRef={nodeRef}
                handle=".drag-handle"
            >
                <div ref={nodeRef} className="bg-[#FAFAFA] dark:bg-[#111111] rounded-lg shadow-2xl w-full max-w-lg border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white pointer-events-auto max-h-[80vh] flex flex-col">
                    {/* Header */}
                    <div className="drag-handle cursor-move flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10 flex-shrink-0">
                        <h2 className="text-xl font-semibold italic uppercase tracking-wider select-none">Edit Captions</h2>
                        <button
                            onClick={handleCancel}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6 overflow-y-auto flex-1">
                        {/* Edit Currently Visible Caption */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 uppercase tracking-tighter mb-3">
                                Current Caption
                            </label>

                            {activeParagraph ? (
                                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                                    <input
                                        type="text"
                                        value={(activeParagraph.words || []).map(w => w.content).join(' ')}
                                        onChange={(e) => handleSentenceChange(e.target.value)}
                                        className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                        placeholder="Edit caption text..."
                                    />
                                </div>
                            ) : (
                                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-center text-gray-500 text-sm">
                                    No caption visible at current time. Move the playhead to a caption.
                                </div>
                            )}
                        </div>

                        {/* Font Size */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400 uppercase tracking-tighter">Text Size</label>
                            <div className="flex items-center gap-4">
                                <input
                                    type="range"
                                    min="10"
                                    max="150"
                                    value={fontSize}
                                    onChange={(e) => {
                                        const newSize = parseInt(e.target.value);
                                        setFontSize(newSize);
                                        if (onPreviewUpdate) {
                                            onPreviewUpdate({
                                                ...(appearance || {}),
                                                scale: newSize / 50
                                            });
                                        }
                                    }}
                                    className="flex-1 h-2 bg-gray-200 dark:bg-gray-800 rounded-lg appearance-none cursor-pointer accent-orange-500"
                                />
                                <span className="w-12 text-center font-mono">{fontSize}</span>
                            </div>
                        </div>

                        {/* Font Family */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400 uppercase tracking-tighter">Font Family</label>
                            <select
                                value={fontFamily}
                                onChange={(e) => setFontFamily(e.target.value)}
                                className="w-full bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 text-gray-900 dark:text-white"
                            >
                                <option value="Arial Black">Arial Black (Bold)</option>
                                <option value="Impact">Impact (Classic)</option>
                                <option value="Helvetica">Helvetica</option>
                                <option value="Verdana">Verdana</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Courier New">Courier New</option>
                            </select>
                        </div>

                        {/* Highlight Color */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400 uppercase tracking-tighter">Active Word Highlight</label>
                            <div className="flex items-center gap-3">
                                {[
                                    '#ffea00', // Yellow
                                    '#00ff00', // Green
                                    '#ff00ff', // Magenta
                                    '#00ffff', // Cyan
                                    '#ffffff', // White
                                    '#ff8800'  // Orange
                                ].map(color => (
                                    <button
                                        key={color}
                                        onClick={() => setHighlightBg(color)}
                                        className={`w-8 h-8 rounded-full border-2 transition-transform ${highlightBg === color ? 'border-orange-500 scale-110' : 'border-transparent'}`}
                                        style={{ backgroundColor: color }}
                                    />
                                ))}
                                <input
                                    type="color"
                                    value={highlightBg}
                                    onChange={(e) => setHighlightBg(e.target.value)}
                                    className="w-8 h-8 rounded-full bg-transparent cursor-pointer ml-auto"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-white/10 flex-shrink-0">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-6 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors text-white font-bold uppercase tracking-widest"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default CaptionsCustomizationModal;
